import { config } from '@v4/config';
import { getJsonDataFromScript } from '@v4/common/utils/json';

/**
 * Get locale from lang attribute in the HTML tag
 *
 * @returns {string} locale value, by default it's `en`
 */
export const getLocale = () => (document.documentElement.lang || config.DEFAULT_LANG).substr(0, 2);

/**
 * Get the country code when it differs from the language code (Austria shares `de` with Germany)
 *
 * @returns {string} `at` for the Austrian site, otherwise the same value as `getLocale`
 */
export const getCountryCode = () =>
    (document.documentElement.lang || '').toLowerCase() === 'de-at' ? 'at' : getLocale();

const DEFAULT_ZIP_RULES = { type: 'number', minLength: 5, maxLength: 5, pattern: '[0-9]{5}', strict: false };

// Countries with a postal code different from the default 5 digits.
// `strict` rules are checked against `pattern` too, the others only against the length.
const ZIP_RULES = {
    at: { type: 'number', minLength: 4, maxLength: 4, pattern: '[0-9]{4}', strict: false },
    // Netherlands: 4 digits (not starting with 0), the letters of the postal code (e.g. 1012 AB) are not asked
    nl: { type: 'number', minLength: 4, maxLength: 4, pattern: '[1-9][0-9]{3}', strict: true },
};

/**
 * Get the postal code rules of the current country
 *
 * @returns {{type: string, minLength: number, maxLength: number, pattern: string, strict: boolean}} zip code rules
 */
export const getZipRules = () => ZIP_RULES[getCountryCode()] || DEFAULT_ZIP_RULES;

/**
 * Get the maximum length of the postal code for the current country (Austria and Netherlands use 4, the others 5)
 *
 * @returns {number} zip code length
 */
export const getZipLength = () => getZipRules().maxLength;

/**
 * Get the attributes of the postal code input for the current country
 *
 * @returns {{type: string, minlength: number, maxlength: number, pattern: string}} input attributes
 */
export const getZipInputAttributes = () => {
    const { type, minLength, maxLength, pattern } = getZipRules();

    return { type, minlength: minLength, maxlength: maxLength, pattern: `^${pattern}$` };
};

/**
 * Get locale messages from script block with type 'application/json'
 *
 * @returns {object} messages
 */
export const getLocaleMessages = (key = 'DrivekDictionary') => {
    const selector = 'script#drivek-translations[type="application/json"]';
    let messages;

    if (document.querySelector(selector)) {
        messages = getJsonDataFromScript(selector)[key];
    }

    return messages;
};
